/*
 * FilterPanel — 全站统一表格筛选面板
 * 规范基线：/admin/userList，详见 docs/规范.md
 *
 * 用法：
 *   <FilterPanel
 *     form={form}
 *     onSearch={handleSearch}
 *     onReset={handleReset}
 *     extra={<Button onClick={handleExport}>导出</Button>}
 *   >
 *     <Form.Item label="审核状态" name="state">
 *       <Select mode="multiple" options={...} />
 *     </Form.Item>
 *     <Form.Item label="时间范围" name="DateRange" className={FilterPanel.span2}>
 *       <DatePicker.RangePicker />
 *     </Form.Item>
 *   </FilterPanel>
 *
 * 规范：
 *   - 卡片白底 / 圆角 8px
 *   - 固定 4 列网格、不够换行；每个 Form.Item 标签在上
 *   - 跨 2 列加 className={FilterPanel.span2}
 *   - 按钮在网格内最后一格、右对齐：查询(primary) → 重置 → 业务按钮(extra)
 *   - 表单内按 Enter = 触发查询
 */
import React from 'react';
import { Button, Form } from 'antd';
import type { FormInstance } from 'antd';
import styles from './index.scss';

export interface FilterPanelProps {
  /** antd Form instance */
  form: FormInstance;
  /** 「查询」点击；通常 = read form values → set applied filter → reload table */
  onSearch: () => void;
  /** 「重置」点击；通常 = form.resetFields() + set applied = {} + reload */
  onReset: () => void;
  /** 右侧业务按钮（导出、批量操作等），紧跟在「重置」之后 */
  extra?: React.ReactNode;
  /** 查询按钮文案，默认「查询」 */
  searchText?: string;
  /** 重置按钮文案，默认「重置」 */
  resetText?: string;
  /** 表单字段（Form.Item 列表） */
  children: React.ReactNode;
}

const FilterPanel: React.FC<FilterPanelProps> & { span2: string } = ({
  form,
  onSearch,
  onReset,
  extra,
  searchText = '查询',
  resetText = '重置',
  children,
}) => {
  return (
    <div className={styles.panel}>
      <Form
        form={form}
        layout="vertical"
        className={styles.grid}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
          const target = e.target as HTMLElement;
          // textarea 里的换行、Select/DatePicker 下拉打开时的选项确认，都不算「执行查询」
          if (target.tagName === 'TEXTAREA') return;
          if (
            target.closest('.ant-select')?.classList.contains('ant-select-open') ||
            document.querySelector('.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)')
          ) {
            return;
          }
          e.preventDefault();
          onSearch();
        }}
      >
        {children}
        <div className={styles.footer}>
          <Button type="primary" onClick={onSearch}>{searchText}</Button>
          <Button onClick={onReset}>{resetText}</Button>
          {extra}
        </div>
      </Form>
    </div>
  );
};

/** 给 Form.Item 加这个 className 让它在网格中跨 2 列（适合日期范围等长字段） */
FilterPanel.span2 = styles.span2;

export default FilterPanel;
